import { useMemo, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ListChecks, ShieldAlert } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState } from '@/components/data/EmptyState'
import { AuditLogFilters } from '@/components/audit/AuditLogFilters'
import { AuditLogTable } from '@/components/audit/AuditLogTable'
import { AuditEntryDrawer } from '@/components/audit/AuditEntryDrawer'
import { useAuditLogList } from '@/hooks/api/useAuditLog'
import { useRoles } from '@/hooks/useRoles'
import { ApiError } from '@/lib/api'
import type { AuditLogListFilters } from '@/types/audit-log'
import { searchString } from '@/lib/search-params'

export const Route = createFileRoute('/audit-log')({
    component: AuditLogPage,
    validateSearch: (search): AuditLogListFilters => ({
        actor: searchString(search.actor),
        action: searchString(search.action),
        subjectType: searchString(search.subjectType ?? search.subject_type),
        subjectId: searchString(search.subjectId ?? search.subject_id),
        from: searchString(search.from),
        to: searchString(search.to),
    }),
})

function AuditLogPage() {
    const filters = Route.useSearch()
    const navigate = useNavigate({ from: Route.fullPath })
    const { hasAnyRole } = useRoles()
    const [openId, setOpenId] = useState<string | null>(null)

    if (!hasAnyRole('admin', 'mlro', 'read_only')) {
        return (
            <EmptyState
                icon={ShieldAlert}
                title="No access"
                description="Your operator session does not include a role that can view the audit log."
            />
        )
    }

    const setFilters = (next: AuditLogListFilters) => {
        navigate({
            // `replace: true` so filter tweaks don't spam the history stack.
            search: () => next,
            replace: true,
        })
    }

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold">Audit log</h1>
                <p className="text-muted-foreground text-sm">
                    Append-only record of every policy change, manual override, and registry
                    mutation made through the operator console. Retained 7 years per the KYC/AML
                    policy.
                </p>
            </div>

            <AuditLogFilters value={filters} onChange={setFilters} />

            <Card>
                <CardHeader>
                    <CardTitle>Entries</CardTitle>
                    <CardDescription>
                        Newest first. Click a row to inspect the full before/after payload.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <AuditLogBody filters={filters} onOpenEntry={(id) => setOpenId(id)} />
                </CardContent>
            </Card>

            <AuditEntryDrawer
                entryId={openId}
                open={openId !== null}
                onOpenChange={(open) => {
                    if (!open) setOpenId(null)
                }}
            />
        </div>
    )
}

function AuditLogBody({
    filters,
    onOpenEntry,
}: {
    filters: AuditLogListFilters
    onOpenEntry: (id: string) => void
}) {
    const query = useAuditLogList(filters)
    const items = useMemo(() => query.data?.pages.flatMap((p) => p.entries) ?? [], [query.data])

    if (query.isLoading) return <Skeleton className="h-40 w-full" />

    if (query.isError) {
        const err = query.error
        const unauthorized = err instanceof ApiError && err.status === 401
        return (
            <EmptyState
                icon={ListChecks}
                title={unauthorized ? 'Session expired' : 'Could not load audit log'}
                description={
                    unauthorized
                        ? 'Reload the page to re-authenticate with your wallet.'
                        : err instanceof Error
                          ? err.message
                          : 'Unknown error'
                }
            />
        )
    }

    if (items.length === 0) {
        return (
            <EmptyState
                icon={ListChecks}
                title="No entries"
                description="No audit entries match these filters."
            />
        )
    }

    return (
        <div className="space-y-3">
            <AuditLogTable items={items} onRowClick={onOpenEntry} />
            <div className="flex justify-center">
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => query.fetchNextPage()}
                    disabled={!query.hasNextPage || query.isFetchingNextPage}
                >
                    {query.isFetchingNextPage
                        ? 'Loading…'
                        : query.hasNextPage
                          ? 'Load more'
                          : 'End of history'}
                </Button>
            </div>
        </div>
    )
}
